<script setup lang="ts">
import { computed } from 'vue'
import type { Team, Personnel } from '@/types'
import { MedicalQual } from '@/types'
import FosTooltip from '@/components/ui/FosTooltip.vue'
import { useDragState } from '@/composables/useDragState'
import { usePersonnelStore } from '@/stores/personnel'

const props = withDefaults(
  defineProps<{
    team: Team
    side?: 'top' | 'bottom' | 'left' | 'right'
  }>(),
  { side: 'right' },
)

const personnelStore = usePersonnelStore()

const teamMembers = computed(() =>
  personnelStore.roster
    .filter(p => p.team_id === props.team.team_id)
    .sort((a, b) => {
      if (a.personnel_id === props.team.team_leader_personnel_id) return -1
      if (b.personnel_id === props.team.team_leader_personnel_id) return 1
      if (a.time_out === null && b.time_out !== null) return -1
      if (a.time_out !== null && b.time_out === null) return 1
      return 0
    })
)

const EMT_AND_ABOVE = new Set<string>([MedicalQual.EMT, MedicalQual.NurseParamedic, MedicalQual.MD])

function isMedic(person: Personnel): boolean {
  return EMT_AND_ABOVE.has((person.qualifications?.medical_qual ?? '') as string)
}

function getQualChips(person: Personnel): { label: string; highlight: boolean }[] {
  const q = person.qualifications ?? {}
  const chips: { label: string; highlight: boolean }[] = []
  if (q.medical_qual && q.medical_qual !== 'None') {
    chips.push({ label: q.medical_qual as string, highlight: EMT_AND_ABOVE.has(q.medical_qual as string) })
  }
  if (q.searcher_type && q.searcher_type !== 'None') chips.push({ label: q.searcher_type as string, highlight: false })
  if (q.rope_qual && q.rope_qual !== 'None') chips.push({ label: q.rope_qual as string, highlight: false })
  if (Array.isArray(q.misc_quals)) {
    chips.push(...(q.misc_quals as string[]).map(l => ({ label: l, highlight: false })))
  }
  return chips
}

const { isDragging } = useDragState()

const sortedMembers = computed(() => {
  return [...teamMembers.value].sort((a, b) => {
    if (a.personnel_id === props.team.team_leader_personnel_id) return -1
    if (b.personnel_id === props.team.team_leader_personnel_id) return 1
    return 0
  })
})

</script>

<template>
  <FosTooltip side="right">
    <slot />
    <template #content>
      <div class="flex flex-col gap-1.5">
        <span class="text-[0.6rem] uppercase tracking-widest text-surface/50">{{ teamMembers.length }} member{{ teamMembers.length !== 1 ? 's' : '' }}</span>
        <div class="h-px bg-surface/20" />
        <div
          v-for="teamMember in sortedMembers"
          :key="teamMember.personnel_id"
          class="flex flex-col gap-0.5"
          :class="teamMember.time_out !== null ? 'opacity-50' : ''"
        >
          <span class="text-surface leading-tight flex items-center gap-1">
            {{ teamMember.first }} {{ teamMember.last }}
            <span v-if="teamMember.personnel_id === team.team_leader_personnel_id" class="text-yellow-400 text-[0.6rem]">★</span>
            <span v-if="teamMember.time_out !== null" class="text-[0.6rem] font-mono uppercase tracking-wide">out</span>
          </span>
          <span class="text-surface/50 text-[0.6rem]">{{ teamMember.agency }}</span>
          <span v-if="getQualChips(teamMember).length > 0" class="text-surface/60 text-[0.6rem]">
            <template v-for="(chip, i) in getQualChips(teamMember)" :key="chip.label">
              <span v-if="i > 0"> · </span>
              <span :class="chip.highlight ? 'text-emerald-400 font-semibold' : ''">{{ chip.label }}</span>
            </template>
          </span>
        </div>
      </div>
    </template>
  </FosTooltip>
</template>
